import React from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import constants from '@tcp/core/src/components/features/account/Orders/Orders.constants';
import DamImage from '@tcp/core/src/components/common/atoms/DamImage';
import { getRCStatus } from '@tcp/core/src/components/features/account/OrderDetails/organism/OrderDetailsWrapper/views/OrderDetailsWrapper.utils';
import { orderDateFormatted, capitalizeWords, getDateObject } from '@tcp/core/src/utils/utils';
import internalEndpoints from '@tcp/core/src/components/features/account/common/internalEndpoints';
import orderConfig from '@tcp/core/src/config/orderConfig';
import BodyCopy from '../../../../../../common/atoms/BodyCopy';
import Anchor from '../../../../../../common/atoms/Anchor';
import withStyles from '../../../../../../common/hoc/withStyles';
import styles from '../styles/OrderDetail.style';
import {
  getLabelFromCopyOrFromTranslation,
  getLabelWithDefaultValue,
} from '../../../../../../../utils/labels/labelUtilty';
import {
  ACCOUNT_ORDERS_LABEL,
  OrderDetailLabels,
  BROWSE_PDP_LABEL,
  lblWriteReviewLocation,
  OrderTileDetailsLabels,
  lblOrderStatusProcessing,
  lblOrdersShipped,
  lblStatusOrderReadyForPickup,
  lblCshCancelled,
  lblStatusOrderExpired,
  lblOrdersStatusItemsPickedUp,
  lblOrdersReturnsInitiated,
  lblOrdersReturnsCompleted,
  lblOrdersReturnInitiated,
} from '../../../../OrderDetails/container/OrderDetails.labels';
import {
  GLOBAL_GENERIC_LABELS,
  GlobalGenericLabels,
  lblDetails,
} from '../../../../../../../utils/labels/globalGeneric.labels';

const ORDER_TILE_ITEM_IMG = 't_pdp_imgswatch,f_auto';
const { STATUS_CONSTANTS = {} } = constants;
const {
  PICKUP_EXPIRED,
  PICKUP_ITEM_EXPIRED,
  ORDER_CANCELLED,
  SHIPPED,
  PROCESSING,
  CANCELED,
  ORDER_IN_PROCESS,
  ORDER_PROCESSING,
  ITEMS_READY_FOR_PICKUP,
  ITEM_READY_FOR_PICK_UP,
  CANCELLED,
  ORDER_CANCELED,
  EXPIRED,
  SUCCESSFULLY_PICKED_UP,
  ITEMS_PICKED_UP,
  RETURNS_COMPLETED,
  RETURNS_INITIATED,
  RETURN_INITIATED,
  RETURN_COMPLETED,
} = STATUS_CONSTANTS;

const SHOW_MORE_LENGTH = 4;

const getStatusDateClass = (status) => {
  switch (status) {
    case ORDER_CANCELLED:
    case PICKUP_EXPIRED:
    case PICKUP_ITEM_EXPIRED:
    case ORDER_CANCELED:
      return 'order-status-date-expire';
    default:
      return 'order-status-date-default';
  }
};

const getStatusClass = (status) => {
  return status.toLowerCase().trim() === ORDER_CANCELLED ||
    status.toLowerCase().trim() === PICKUP_EXPIRED ||
    status.toLowerCase().trim() === PICKUP_ITEM_EXPIRED ||
    status.toLowerCase().trim() === CANCELED
    ? 'order-status-expire'
    : 'order-status-default';
};

const getDefaultStatus = (status, orderLabels) => {
  const labelKey = status ? `lbl_status_${status.replace(/ /g, '_')}` : '';
  return orderLabels[labelKey] || status;
};

const getStatus = (status, orderLabels) => {
  switch (status) {
    case PROCESSING:
    case ORDER_IN_PROCESS:
    case ORDER_PROCESSING:
      return getLabelWithDefaultValue(orderLabels, lblOrderStatusProcessing);
    case SHIPPED:
      return getLabelWithDefaultValue(orderLabels, lblOrdersShipped);
    case ITEMS_READY_FOR_PICKUP:
    case ITEM_READY_FOR_PICK_UP:
      return getLabelWithDefaultValue(orderLabels, lblStatusOrderReadyForPickup);
    case CANCELLED:
    case ORDER_CANCELED:
    case ORDER_CANCELLED:
      return getLabelWithDefaultValue(orderLabels, lblCshCancelled);
    case EXPIRED:
      return getLabelWithDefaultValue(orderLabels, lblStatusOrderExpired);
    case SUCCESSFULLY_PICKED_UP:
    case ITEMS_PICKED_UP:
      return getLabelWithDefaultValue(orderLabels, lblOrdersStatusItemsPickedUp);
    case RETURNS_INITIATED:
      return getLabelWithDefaultValue(orderLabels, lblOrdersReturnsInitiated);
    case RETURNS_COMPLETED:
      return getLabelWithDefaultValue(orderLabels, lblOrdersReturnsCompleted);
    case RETURN_INITIATED:
      return getLabelWithDefaultValue(orderLabels, lblOrdersReturnsInitiated);
    case RETURN_COMPLETED:
      return getLabelWithDefaultValue(orderLabels, lblOrdersReturnInitiated);
    default:
      return getDefaultStatus(status, orderLabels);
  }
};

const getOrderStatus = (orderLabels, baseOrderStatus) => {
  const statusLowerCase = baseOrderStatus?.toLowerCase() || '';
  const mappedOrderStatus = getStatus(statusLowerCase, orderLabels);
  let orderStatus = mappedOrderStatus;

  if (mappedOrderStatus.includes('lbl_')) {
    const orderStatusTextFromLabel = getLabelWithDefaultValue(orderLabels, baseOrderStatus);
    orderStatus = getStatus(orderStatusTextFromLabel.toLowerCase(), orderLabels);
  }

  return orderStatus;
};

const getDate = (date) => (date ? orderDateFormatted(date) : '');

const getStoreName = (pickupLocation) => {
  const [storeName] = pickupLocation.split('|');
  return capitalizeWords(storeName);
};

// eslint-disable-next-line sonarjs/cognitive-complexity
const OrderTileDetail = (props) => {
  const { className, orderItem, useRouterNavigation } = props;
  const {
    orderType,
    pickupLocation,
    trackingNbr,
    items,
    status,
    estimatedDeliveryDate,
    estimatedShipmentDate,
    orderNbr,
    orderStatus,
  } = orderItem;
  const orderLabels = getLabelFromCopyOrFromTranslation(ACCOUNT_ORDERS_LABEL, OrderDetailLabels);

  const shipmentStatus = isEmpty(status) || status === 'null' ? orderStatus : status; // For fallback scenario we are keeping orderStatus if status come null or undefined;
  const mappedOrderStatus = getOrderStatus(orderLabels, shipmentStatus);
  const storeName = getStoreName(pickupLocation);

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const getDateWithLabel = () => {
    if (isEmpty(items)) {
      return '';
    }

    const [item] = items;
    const shipmentAndStatusInfo = item?.shipmentAndStatusInfo || [];
    const [shipment] = shipmentAndStatusInfo;
    const actualDeliveryDateValue = shipment?.actualDeliveryDate;
    const carrierDeliveryDateValue = shipment?.carrierDeliveryDate;
    const expectedDeliveryDateValue = estimatedDeliveryDate;
    const estimatedShipmentDateValue = estimatedShipmentDate;
    const shipDateDateValue = shipment?.shipDate;
    const statusText = status.toLowerCase();

    const { label, displayDate } =
      getRCStatus(
        statusText,
        expectedDeliveryDateValue,
        actualDeliveryDateValue,
        shipDateDateValue,
        carrierDeliveryDateValue,
        orderLabels,
        estimatedShipmentDateValue
      ) || {};

    if (isEmpty(label) || isEmpty(displayDate)) {
      return '';
    }

    const dateString =
      typeof displayDate === 'string' ? displayDate.replace(/-/g, '/') : displayDate;
    const displayDateObject = getDateObject(dateString);
    const formattedDisplayDate = getDate(displayDateObject);

    return label && formattedDisplayDate ? (
      <BodyCopy
        fontSize="fs14"
        fontFamily="secondary"
        fontWeight="bold"
        component="div"
        className={`order-status-date ${getStatusDateClass(statusText)}`}
      >{`${label} ${formattedDisplayDate}`}</BodyCopy>
    ) : null;
  };

  const getThumbnailimgPath = (imgURL) => {
    const slashIndex = imgURL?.lastIndexOf('/');
    const imagePath = imgURL?.substring(slashIndex + 1);
    return imagePath ? `${imagePath.split('.')[0].split('_')[0]}/${imagePath}` : '';
  };

  return (
    <BodyCopy key={trackingNbr} component="div" className={`${className} order-detail-tile`}>
      <BodyCopy component="div" className="tileWrapper order-detail-tileWrapper">
        <BodyCopy component="div" className="product-image-container">
          <BodyCopy component="div" className="images-container">
            {items.slice(0, SHOW_MORE_LENGTH).map((product) => (
              <DamImage
                imgData={{
                  url: getThumbnailimgPath(product.imgURL),
                  alt: 'Product',
                }}
                imgConfigs={[ORDER_TILE_ITEM_IMG, ORDER_TILE_ITEM_IMG, ORDER_TILE_ITEM_IMG]}
                isProductImage
                width="47px"
                height="57px"
                className={'product-image'}
                itemBrand={product.brand}
              />
            ))}
          </BodyCopy>
          {items.length > SHOW_MORE_LENGTH && (
            <BodyCopy component="div" className="more-product-counts">
              <BodyCopy component="div" className="show-more-numbers">
                <span>+{items.length - SHOW_MORE_LENGTH}</span>
              </BodyCopy>
            </BodyCopy>
          )}
        </BodyCopy>
        <BodyCopy component="div" className="order-status-details">
          <BodyCopy
            className={`order-status ${getStatusClass(mappedOrderStatus)}`}
            component="div"
            fontSize="fs16"
            fontFamily="secondary"
            fontWeight="bold"
          >
            {mappedOrderStatus}
          </BodyCopy>
          <BodyCopy component="div">
            <Anchor
              fontSizeVariation="large"
              underline
              anchorVariation="primary"
              to={`${internalEndpoints.orderPage.link}&orderId=${orderNbr}`}
              asPath={`${internalEndpoints.orderPage.path}/${orderNbr}`}
              aria-label="Order Detail"
              className="order-detail-link"
              useRouterNavigation={useRouterNavigation}
            >
              {getLabelFromCopyOrFromTranslation(
                `${GLOBAL_GENERIC_LABELS}.${lblDetails}`,
                GlobalGenericLabels
              )}
            </Anchor>
          </BodyCopy>
        </BodyCopy>
        {!orderConfig.NON_ECOM_ORDERS.includes(orderType) && <>{getDateWithLabel()}</>}
        {orderConfig.NON_ECOM_ORDERS.includes(orderType) && pickupLocation && (
          <BodyCopy component="div" className="order-pickup-container">
            <BodyCopy
              component="span"
              fontSize="fs14"
              fontFamily="secondary"
              fontWeight="bold"
              className="order-pickup-location"
            >{`${getLabelFromCopyOrFromTranslation(
              `${BROWSE_PDP_LABEL}.${lblWriteReviewLocation}`,
              OrderTileDetailsLabels
            )}:`}</BodyCopy>
            <BodyCopy
              component="span"
              className="pickup-location-detail-text"
              fontSize="fs14"
              fontFamily="secondary"
              fontWeight="bold"
            >
              {storeName}
            </BodyCopy>
          </BodyCopy>
        )}
      </BodyCopy>
    </BodyCopy>
  );
};

OrderTileDetail.propTypes = {
  className: PropTypes.string,
  orderItem: PropTypes.shape({}),
  useRouterNavigation: PropTypes.bool,
};

OrderTileDetail.defaultProps = {
  className: '',
  useRouterNavigation: false,
};

export default withStyles(OrderTileDetail, styles);
export { OrderTileDetail as OrderTileDetailVanilla };
